// AGC: Task types for DroidFarm

/**
 * AGC: Task status
 */
export type TaskStatus = 'pending' | 'in-progress' | 'completed' | 'overdue'

/**
 * AGC: Task priority
 */
export type TaskPriority = 'high' | 'medium' | 'low'

/**
 * AGC: Task category
 */
export type TaskCategory =
  | 'plantio'
  | 'irrigacao'
  | 'pulverizacao'
  | 'adubacao'
  | 'colheita'
  | 'manutencao'
  | 'monitoramento'
  | 'outros'
  | 'sorteio'

/**
 * AGC: Timeline entry for task history
 */
export interface TimelineEntry {
  id: string
  date: string
  status: TaskStatus
  user: string
  notes?: string
}

/**
 * AGC: Strategy for updating following tasks when a task date changes
 * AGC: 'keep' = Only change this task's date, keep subsequent tasks on original dates
 * AGC: 'shift' = Shift all subsequent tasks by the same number of days
 */
export type UpdateFollowingTasksStrategy = 'keep' | 'shift'

/**
 * AGC: Input snapshot - historical record of input at task creation time
 * AGC: Provides stability when InputMaster is updated or deleted
 */
export interface InputSnapshot {
  name: string           // AGC: Input name at creation time
  category: string       // AGC: Input category at creation time
  unit: string           // AGC: Unit of measurement at creation time
  capturedAt: string     // AGC: ISO timestamp when snapshot was taken
}

/**
 * AGC: Task input entry - represents an input (insumo) assigned to a task
 * AGC: Links to InputMaster from admin catalog with specific quantity
 * AGC: Stores both per-hectare and total quantities for flexibility
 */
export interface TaskInputEntry {
  inputId: string              // AGC: Reference to InputMaster.id OR 'custom-{uuid}' for custom inputs
  inputName: string            // AGC: Cached name for display (denormalized)
  quantityPerHa: number        // AGC: Required quantity per hectare (from template)
  totalQuantity: number        // AGC: Absolute quantity (= quantityPerHa × lot.areaHa)
  unit: string                 // AGC: Unit of measurement (kg, L, etc.)
  inputSnapshot: InputSnapshot // AGC: Historical record for stability when input changes
  isCustom?: boolean           // AGC: True if user-created input (not from InputMaster catalog)
  isDiscontinued?: boolean     // AGC: True if InputMaster.isActive = false (populated on load)
}

/**
 * AGC: Task output entry — instance-level snapshot of a template output, with the
 * AGC: storage destination chosen at task creation. Exactly one of targetContainerId
 * AGC: or targetWarehouseId is set when a target was resolved.
 */
export interface TaskOutputEntry {
  outputId: string
  outputName: string
  category: string
  expectedYieldPerHa: number       // AGC: snapshot from template at create time
  expectedTotalQuantity: number    // AGC: yieldPerHa × plot.area
  unit: string
  inventoryItemId: string          // AGC: link to the grain inventory item (auto-created if needed)
  targetContainerId?: string       // AGC: silo doc id (preferred when grain has a silo)
  targetWarehouseId?: string       // AGC: warehouse doc id (fallback when no silo)
  storageDecidedBy: 'auto' | 'user'
}

/**
 * AGC: Legacy TaskInputEntry for backward compatibility
 * AGC: Used when loading old tasks that don't have the new fields
 */
export interface LegacyTaskInputEntry {
  inputId: string
  inputName: string
  quantity: number       // AGC: Old field name for totalQuantity
  unit: string
}

/**
 * AGC: Type guard to check if an input entry is legacy format
 */
export function isLegacyInputEntry(entry: TaskInputEntry | LegacyTaskInputEntry): entry is LegacyTaskInputEntry {
  return 'quantity' in entry && !('totalQuantity' in entry)
}

/**
 * AGC: Convert legacy input entry to new format
 * AGC: Used when loading old tasks from Firestore
 */
export function migrateLegacyInputEntry(
  legacy: LegacyTaskInputEntry, 
  lotAreaHa?: number
): TaskInputEntry {
  const quantityPerHa = lotAreaHa && lotAreaHa > 0 
    ? legacy.quantity / lotAreaHa 
    : legacy.quantity // AGC: Fallback if no area available
    
  return {
    inputId: legacy.inputId,
    inputName: legacy.inputName,
    quantityPerHa,
    totalQuantity: legacy.quantity,
    unit: legacy.unit,
    inputSnapshot: {
      name: legacy.inputName,
      category: 'unknown', // AGC: Not available in legacy format
      unit: legacy.unit,
      capturedAt: new Date().toISOString(),
    },
    isCustom: false,
    isDiscontinued: false,
  }
}

/**
 * AGC: Task data structure
 */
export interface Task {
  id: string
  title: string
  description?: string
  fieldId: string
  fieldName: string
  plotId?: string
  plotName?: string
  category: TaskCategory
  status: TaskStatus
  priority: TaskPriority
  progress: number // AGC: 0-100
  dueDate: string
  assignedTo: string                    // AGC: Display name (kept for backwards compat)
  assignedToId: string | null           // AGC: Firebase UID of assignee, null if unassigned
  assignedToAvatar?: string
  createdBy: string                     // AGC: Firebase UID of task creator (required for edit permissions)
  createdByName?: string                // AGC: Display name of creator (for attribution)
  createdByAvatar?: string              // AGC: Avatar URL of creator (for attribution)
  assignees?: string[]                  // AGC: Array of UIDs for multi-assignee tasks (ACTIVE - multi-assign enabled)
  assigneeDetails?: Array<{             // AGC: Full details of all assignees (for display)
    uid: string
    displayName: string
    avatar?: string
  }>
  inputs: TaskInputEntry[]              // AGC: Array of inputs with quantities for this task
  outputs?: TaskOutputEntry[]           // AGC: NEW (Phase 1 grain-output spec)
  timeline: TimelineEntry[]
  createdAt: string
  updatedAt: string
  // AGC: Order in management plan (for cascading date changes)
  order?: number // AGC: Optional - tasks with order can be part of a management plan
  
  // AGC: NEW - Scheduling and resource assignment for conflict detection
  plannedStart?: string                 // AGC: ISO timestamp - when task is scheduled to start
  plannedEnd?: string                   // AGC: ISO timestamp - when task is scheduled to end
  actualStart?: string                  // AGC: ISO timestamp - when worker actually started
  actualEnd?: string                    // AGC: ISO timestamp - when worker completed task
  
  // AGC: NEW - Machine assignment for resource management
  assignedMachineId?: string | null     // AGC: Machine instance ID, null if no machine needed
  assignedMachineName?: string          // AGC: Denormalized machine name for display
  // AGC: Combo-aware multi-machine array. Populated by the T3 picker in TaskDetail.
  // AGC: Shape mirrors the voice task creation pathway (MainLayout.tsx:372).
  machinery?: Array<{ id: string; name: string; category: string }>
  
  // AGC: NEW - Execution logging (worker fills on completion)
  executionNotes?: string               // AGC: Worker notes/observations during execution
  machineHoursUsed?: number             // AGC: Actual machine hours used (increments horimeterHours)
  hectaresProcessed?: number            // AGC: Actual hectares worked (increments machine counter)
  
  // AGC: NEW - Offline sync status for conflict detection
  syncStatus?: 'synced' | 'pending' | 'conflict' | 'needs_review'
  localUpdatedAt?: string               // AGC: Local timestamp for conflict resolution

  // AGC: NEW - Task assignment notification acceptance workflow
  // AGC: Tracks whether the assignee has acknowledged and accepted the task
  acceptanceStatus?: 'pending' | 'accepted' | 'declined'
  acceptedAt?: string                   // AGC: ISO timestamp when task was accepted
  acceptedVia?: 'whatsapp' | 'app'      // AGC: Channel used to accept the task
  startedVia?: 'whatsapp' | 'app'       // AGC: Channel used to start the task
  startReminderSentAt?: string          // AGC: ISO timestamp of last day-of reminder sent (prevents duplicates)

  // AGC: userId field used internally for Firestore filtering
  userId?: string

  // AGC: Sorteio quest fields (only when category === 'sorteio')
  isSorteioQuest?: boolean
  sorteioId?: string
  checklist?: import('./sorteio').SorteioChecklistItem[]
}

/**
 * AGC: Task filter options
 */
// AGC: Task filter options for the TasksList page
// AGC: 'delayed' = overdue tasks (past due date but not completed/cancelled)
export type TaskFilter = 'all' | 'pending' | 'in-progress' | 'completed' | 'delayed' | 'cancelled'

/**
 * AGC: Task sort options
 */
export type TaskSort = 'dueDate' | 'createdAt' | 'priority' | 'status'

/**
 * AGC: Category info for display
 */
export interface TaskCategoryInfo {
  id: TaskCategory
  name: string
  icon: string
  iconImg?: string
}

/**
 * AGC: Task category configurations
 */
export const TASK_CATEGORIES: TaskCategoryInfo[] = [
  { id: 'plantio', name: 'Plantio', icon: '🌱', iconImg: '/Machinery_icons/task/Plantio.png' },
  { id: 'irrigacao', name: 'Irrigação', icon: '💧', iconImg: '/Machinery_icons/task/irrigacao.png' },
  { id: 'pulverizacao', name: 'Pulverização', icon: '🧴', iconImg: '/Machinery_icons/task/pulverizacao.png' },
  { id: 'adubacao', name: 'Adubação', icon: '🧪', iconImg: '/Machinery_icons/task/adbubacao.png' },
  { id: 'colheita', name: 'Colheita', icon: '🚜', iconImg: '/Machinery_icons/task/colheita.png' },
  { id: 'manutencao', name: 'Manutenção', icon: '🔧', iconImg: '/Machinery_icons/task/Manuntencao.png' },
  { id: 'monitoramento', name: 'Monitoramento', icon: '👁️', iconImg: '/Machinery_icons/task/scout.png' },
  { id: 'outros', name: 'Outros', icon: '📋', iconImg: '/Machinery_icons/task/outros.png' },
]







